import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Tab } from '../../design-system';
import {
  TAB_UPLOADED_MEDIA,
  TAB_EXPORTED,
  TAB_SEARCH,
  TAB_CALENDAR,
  TAB_MEETINGS,
  ROUTE_MY_LIBRARY_CALENDAR,
  ROUTE_MY_LIBRARY_MEETINGS,
  ROUTE_MY_LIBRARY_UPLOADS,
  ROUTE_MY_LIBRARY_EXPORTS,
  ROUTE_MY_LIBRARY_SEARCH,
} from './constants/myLibraryText';
import LookSwitch from './looks/LookSwitch';

interface TabNavigationProps {
  // No props needed - component uses hooks internally
}

// Search All Videos is hidden until it helps people find their videos (2026-11-01). Its page
// still works at /my-library/search/.
const SHOW_SEARCH_TAB = false;

/**
 * Tab Navigation Component
 * 
 * Horizontal tab navigation for My Library page.
 * Tabs: "Uploaded Media" and "Exported"
 * Active tab determined by current route.
 */
const TabNavigation: React.FC<TabNavigationProps> = (): JSX.Element => {
  const location = useLocation();
  const navigate = useNavigate();

  const isUploadsTab = location.pathname === ROUTE_MY_LIBRARY_UPLOADS;
  const isExportsTab = location.pathname === ROUTE_MY_LIBRARY_EXPORTS;
  const isSearchTab = location.pathname === ROUTE_MY_LIBRARY_SEARCH;
  const isCalendarTab = location.pathname === ROUTE_MY_LIBRARY_CALENDAR;
  const isMeetingsTab = location.pathname === ROUTE_MY_LIBRARY_MEETINGS;

  const handleTabClick = (tabPath: string): void => {
    navigate(tabPath);
  };

  return (
    <Box sx={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
      <Tab
        label={TAB_UPLOADED_MEDIA}
        isActive={isUploadsTab}
        onClick={() => handleTabClick(ROUTE_MY_LIBRARY_UPLOADS)}
      />
      <Tab
        label={TAB_EXPORTED}
        isActive={isExportsTab}
        onClick={() => handleTabClick(ROUTE_MY_LIBRARY_EXPORTS)}
      />
      {SHOW_SEARCH_TAB && (
        <Tab
          label={TAB_SEARCH}
          isActive={isSearchTab}
          onClick={() => handleTabClick(ROUTE_MY_LIBRARY_SEARCH)}
        />
      )}
      <Tab
        label={TAB_CALENDAR}
        isActive={isCalendarTab}
        onClick={() => handleTabClick(ROUTE_MY_LIBRARY_CALENDAR)}
      />
      <Tab
        label={TAB_MEETINGS}
        isActive={isMeetingsTab}
        onClick={() => handleTabClick(ROUTE_MY_LIBRARY_MEETINGS)}
      />
      {/* The way back to the new looks (Gallery, Strips), where it's easy to see. */}
      <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
        <LookSwitch dark />
      </Box>
    </Box>
  );
};

export default TabNavigation;
